// @flow
import React, { PureComponent } from "react";
import { connect, Dispatch } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router";
import { History } from "history";
import { fetchSearchResult } from "data/api";
import {
    type SearchResultDTO,
    type SearchItemDTO,
    type SearchRequestDTO,
    type SearchBucketDTO,
    type SearchValueTypeEnum,
    type SearchFilterDTO,
    type SearchSortDTO,
    type SearchResultRowDTO,
    SortDirectionOptions,
    SearchValueType,
    ListViewMode,
    type ListViewModeEnum,
    type ProblemDetails,
    type MapProviderConfig,
    type RasterLocation,
    type ArrowNavigationSetter,
    SORT_BUCKET_NAME,
} from "data/types";
import { togglesUpdateCounterAction, getSearchAction } from "data/actions";
import { getRoute } from "components/ItemUriLink";
import CommandActionHandler from "components/CommandActions";
import SettingValue, { type SettingValueProps } from "containers/SettingValue";
import SearchResult from "components/SearchResult";
import SearchResultMap from "components/SearchResultMap";
import SearchResultCalendar from "components/SearchResultCalendar";
import SearchResultGrid from "components/SearchResultGrid";
import SearchResultFilterBar from "components/SearchResultFilterBar";
import Constants, {
    type ConstantsEnum,
    Feature,
    isFeatureOn,
} from "data/constants";
import Status from "components/Status";
import { produce } from "immer";
import GetDefaultSearchBuckets from "data/FilterBar";
import * as s from "data/reducers/selectors";
import NoData from "components/NoData";
import { SupportGeoMapViewer } from "components/MapViewer/providerSupport";
import { openSearchResultActionPanel } from "components/SearchResultActionPanel";
import { openMultiSelectionActionPanel } from "components/MultiSelectionActionPanel";
import { type MultiSelectionProps } from "containers/MultiSelection";
import withMultiSelection from "containers/MultiSelection";
import styles from "./SearchResult.module.css";
import localForage from "localforage";
import TrackCurrentRow, {
    type TrackCurrentRowProps,
} from "containers/TrackCurrentRow";
import withArrowNavigation from "containers/ArrowNavigation";

type Props = {
    /** react-router History */
    history: History,
    /** the search requested by the user */
    searchItem?: SearchItemDTO,
    /** optional extra filters to set on searchRequest */
    extraFilters?: Array<SearchFilterDTO>,
    /** fetch status */
    status: ConstantsEnum,
    /** fetch error details */
    error?: ProblemDetails,
    /** search results */
    result: ?SearchResultDTO,
    /** the words to highlight */
    terms: Array<string>,
    /** last searchRequest sent to server (from Redux store) */
    searchRequest: SearchRequestDTO,
    /** callback when setting activeRowIndex
     * @param {number} activeRowIndex new row index
     * @param {SearchResultRowDTO} row row object that was clicked
     * @returns {bool} return FALSE to avoid opening DocViewer for Doc itemUris
     */
    onSetActiveRowIndex?: (
        activeRowIndex: number,
        row?: SearchResultRowDTO
    ) => boolean,
    /** callback when searchForm.autoBrowseToItem is true and one result found
     * @default undefined will navigate to the folder view
     * @param {number} activeRowIndex new row index (0)
     * @param {SearchResultRowDTO} row row object that was clicked
     * */
    onAutoBrowseToItem?: (
        activeRowIndex: number,
        row?: SearchResultRowDTO
    ) => void,
    /** search result row to mark as active */
    activeRowIndex: number,
    /** whether the breadcrumbs should allow navigation */
    isBreadcrumbReadonly?: boolean,
    /** current view mode */
    viewModeSetting: SettingValueProps,
    /** current result sorting */
    sortSetting: SettingValueProps,
    /** Redux Dispatch */
    dispatch: Dispatch,
    /** optional specific ViewMode */
    viewMode: ?ListViewMode,
    /** optional whether to show filterbar */
    showFilterBar: ?boolean,
    /** custom mapProvider to use for Map viewMode */
    mapProvider?: MapProviderConfig,
    /** callback when user clicks an empty space on the map (only when useMarker=false & mapProvider.onClick exists) */
    onMapClick?: (location: RasterLocation) => void,
    /** whether to hide More Action or not */
    isMoreHidden: ?boolean,
    /** Pagination Functions */
    pagination: ?ArrowNavigationSetter,
    /** withMultipleSelection additional props */
    multiSelection?: MultiSelectionProps,
    /** TrackCurrentRow: current selected searchresult row */
    sr?: TrackCurrentRowProps,
    /** optional on row click callback (will override default behavior) */
    onRowClick?: (
        e: SyntheticMouseEvent<*>,
        rowIndex: number,
        row: SearchResultRowDTO
    ) => void,
};

type State = {
    aggregations: ?Array<SearchBucketDTO>,
    selected: Array<SearchFilterDTO>,
    scrollToRow: number,
};

export class SearchResultContainer extends PureComponent<Props, State> {
    _isMounted: boolean;

    constructor(props: Props) {
        super(props);
        this._isMounted = false;
        this.state = {
            aggregations: GetDefaultSearchBuckets(),
            selected: [],
            scrollToRow: props.activeRowIndex,
            storedColWidths: {},
            columnMapper: [...(props.result?.cols?.keys() || [])],
        };
    }

    componentDidMount = () => {
        this._isMounted = true;
        if (this.props.viewMode) {
            this._maybeChangeViewMode(this.props.viewMode);
        } else {
            this._maybeChangeViewMode();
        }
        this.props.searchItem &&
            this._fetch(this._prepSearchRequest(this.props.searchItem));
    };

    componentWillUnmount = () => (this._isMounted = false);

    componentDidUpdate = async (prevProps: Props, prevState: State) => {
        /* istanbul ignore else */
        if (
            prevProps.searchItem !== this.props.searchItem &&
            this.props.searchItem !== null
        ) {
            this.props.multiSelection?.disable?.();
            this.props.pagination?.onRowsLoaded?.({}, 0);
            this.setState(
                {
                    scrollToRow: -1,
                    aggregations: GetDefaultSearchBuckets(),
                    selected: [],
                },
                () =>
                    this._fetch(this._prepSearchRequest(this.props.searchItem))
            );
        }

        if (
            prevProps.result !== this.props.result &&
            this.props.status === Constants.OK
        ) {
            const result = this.props.result;

            /* istanbul ignore else */
            if (result != null) {
                if (
                    this.props.result.autoBrowseToItem === true &&
                    result.totalCount === 1
                ) {
                    if (typeof this.props.onAutoBrowseToItem === "function") {
                        this.props.onAutoBrowseToItem(0, result.rows[0]);
                    } else {
                        this.props.history.replace(
                            getRoute({
                                itemUri: result.rows[0].itemUri,
                                isDoc: result.rows[0].isDoc,
                            })
                        );
                    }
                    return;
                }
                // first result page? update counter
                this._updateToggleCounter(result.totalCount);
                /* istanbul ignore next */
                const aggregationsFromResult =
                    result.aggregations != null ? result.aggregations : [];
                let aggregations: Array<SearchBucketDTO> =
                    GetDefaultSearchBuckets()
                        .concat(
                            aggregationsFromResult.filter(
                                // Hide Aggregations from result that are provided via DefaultSearchBuckets
                                (a) =>
                                    !GetDefaultSearchBuckets().some(
                                        (b) =>
                                            b.name.toUpperCase() ===
                                            a.name.toUpperCase()
                                    )
                            )
                        )
                        .sort((a, b) => {
                            var nameA = a.name.toUpperCase(); // ignore upper and lowercase
                            var nameB = b.name.toUpperCase(); // ignore upper and lowercase
                            if (nameA < nameB) {
                                return -1;
                            }
                            /* istanbul ignore else */
                            if (nameA > nameB) {
                                return 1;
                            }

                            // names must be equal
                            /* istanbul ignore next */
                            return 0;
                        });

                // update bucket.selected
                this.state.selected.forEach((filter) => {
                    /* istanbul ignore else */
                    if (filter.value != null) {
                        const selectIndex = aggregations.findIndex(
                            (i) => i.name === filter.name
                        );
                        /* istanbul ignore else */
                        if (selectIndex !== -1) {
                            aggregations[selectIndex].selected = [filter.value];
                        }
                    }
                });

                this._handleMoreRowsLoaded(
                    result.rows,
                    result.totalCount,
                    false,
                    {
                        aggregations,
                    }
                );

                this.props.multiSelection?.disable();

                this._maybeChangeViewMode(
                    this.props.viewMode ||
                        result.listViewMode ||
                        this.props.viewModeSetting.value
                );

                // update column widths if user has changed them
                const colWidths = await localForage.getItem(
                    `searchResultLayoutColumns_${btoa(
                        this.props.searchRequest?.id
                    )}`
                );

                this.setState({
                    storedColWidths: colWidths || /* istanbul ignore next */ {},
                });

                // update column order if user has changed it
                const colOrder = await localForage.getItem(
                    `searchResultLayoutColumnsOrder_${btoa(
                        this.props.searchRequest?.id
                    )}`
                );

                if (colOrder && colOrder.length === result.cols?.length) {
                    this.setState({ columnMapper: colOrder });
                } else {
                    this.setState({
                        columnMapper: [
                            ...(result.cols?.keys() ||
                                /* istanbul ignore next */ []),
                        ],
                    });
                }
                // clear stored order if the number of columns has changed
                /* istanbul ignore else */
                if (colOrder && colOrder.length !== result.cols?.length) {
                    console.warn(
                        "Column order cannot be applied, because the number of columns has changed. Resetting column order."
                    );
                    localForage.removeItem(
                        `searchResultLayoutColumnsOrder_${btoa(
                            this.props.searchRequest?.id
                        )}`
                    );
                }
            }
        }
    };

    _updateToggleCounter(count: number, loading?: boolean): void {
        let { dispatch } = this.props;
        let status = Constants.OK;
        if (loading === true) {
            status = Constants.LOADING;
        }
        /** Need to update both toggles for consistent UX */
        dispatch(
            togglesUpdateCounterAction({
                name: "searchresult",
                counts: { result: count },
                status: { result: status },
            })
        );
        dispatch(
            togglesUpdateCounterAction({
                name: "search",
                counts: { result: count },
                status: { result: status },
            })
        );
    }

    _prepSearchRequest = (searchItem: SearchItemDTO): SearchRequestDTO => {
        // let's build a searchRequest from an existing SearchItem
        const searchRequest: SearchRequestDTO = {
            pageNum: 0,
            id: searchItem.id,
            name: searchItem.name,
            searchType: searchItem.searchType,
            isCustom: false,
            searchText: searchItem.searchText,
            itemUri: searchItem.itemUri,
            scope: searchItem.scope,
            filters: (searchItem.filters || []).concat(
                this.props.extraFilters || []
            ),
            sorts: searchItem.sorts || [],
            nodeType: searchItem.nodeType,
            fakeUris: searchItem.fakeUris,
        };
        // any filterBar filters from searchItem? update bucket.selected
        const bucketFilters = searchRequest.filters.filter(
            (f) => f.valueType !== SearchValueType.NotSet && f.value != null
        );
        /** istanbul ignore else */
        if (bucketFilters.length > 0) {
            this.setState((prevState: state) => ({
                selected: prevState.selected.concat(bucketFilters),
            }));
        }
        return searchRequest;
    };

    // load first page of results
    _fetch(searchRequest: SearchRequestDTO, hideLoading?: boolean) {
        this._updateToggleCounter(0, true);
        this.props.dispatch(getSearchAction({ searchRequest }));
    }

    // load next page of results
    loadMoreRows = (pageNum: number): Promise<SearchResultDTO> => {
        const { searchRequest, result } = this.props;
        /** Trigger loading only on MapViewMode, due to totalCount on ListView */
        const hasMapViewMode =
            this.props.viewModeSetting.value === ListViewMode.Map;
        this._updateToggleCounter(
            /*istanbul ignore next */ result && !hasMapViewMode
                ? result.totalCount
                : 0,
            hasMapViewMode
        );

        // TODO use Redux action instead of directly loading from fetch!
        return fetchSearchResult(
            produce(searchRequest, (draft) => {
                draft.pageNum = pageNum;
            })
        ).then((result) => {
            /* istanbul ignore next */
            if (!this._isMounted) {
                return;
            }
            this._handleMoreRowsLoaded(result.rows, result.totalCount, true);
            return result;
        });
    };

    onReorder = (oldIndex, newIndex) => {
        const newColumnMapper = [...this.state.columnMapper];
        const [removed] = newColumnMapper.splice(oldIndex, 1);
        newColumnMapper.splice(newIndex, 0, removed);

        localForage.setItem(
            `searchResultLayoutColumnsOrder_${btoa(
                this.props.searchRequest?.id
            )}`,
            newColumnMapper
        );

        this.setState({ columnMapper: newColumnMapper });
    };

    _onFilterBarSelect = (
        name: string,
        valueType: SearchValueTypeEnum,
        value: any
    ) => {
        const { selected } = this.state;
        const { searchRequest } = this.props;
        let newSelected = selected;
        let newSearchRequest = searchRequest;

        // changing a previous loaded one?
        /* istanbul ignore else */
        if (searchRequest.isCustom === false) {
            newSearchRequest = produce(searchRequest, (draft) => {
                draft.isCustom = true;
            });
        }

        // do we already a filter set?
        const selectIndex = selected.findIndex((i) => i.name === name);
        const filterIndex = searchRequest.filters.findIndex(
            (i) => i.name === name
        );
        if (filterIndex !== -1) {
            newSearchRequest = produce(searchRequest, (draft) => {
                draft.filters.splice(filterIndex, 1);
            });
        }

        /* istanbul ignore else */
        if (selectIndex !== -1) {
            newSelected = produce(selected, (draft) => {
                draft.splice(selectIndex, 1);
            });
        }

        /* istanbul ignore else */
        if (value === "__ALL__" && filterIndex !== -1) {
            // enum value? set to 0 (NotSet/Unknown) instead of deleting it (otherwise it cannot overwrite any seachForm additionalParameters)
            if (
                [
                    SearchValueType.ItemType,
                    SearchValueType.FileSize,
                    SearchValueType.RelativeDate,
                ].includes(searchRequest.filters[filterIndex].valueType)
            ) {
                newSearchRequest = produce(searchRequest, (draft) => {
                    draft.filters[filterIndex].value = 0;
                });
            }
        }

        /* istanbul ignore else */
        if (value !== "__ALL__") {
            // add new filter
            newSelected = newSelected.concat([{ name, value, valueType }]);
            if (name !== SORT_BUCKET_NAME) {
                const newFilter: SearchFilterDTO = {
                    name,
                    valueType,
                    value,
                };
                newSearchRequest = produce(newSearchRequest, (draft) => {
                    draft.filters.push(newFilter);
                });
            } else {
                const newSort: SearchSortDTO = {
                    name: value,
                    valueType,
                    direction:
                        searchRequest.sorts &&
                        searchRequest.sorts[0] &&
                        searchRequest.sorts[0].direction ===
                            SortDirectionOptions.Ascending
                            ? SortDirectionOptions.Descending
                            : SortDirectionOptions.Ascending,
                };
                // TODO support multiple sort columns?
                newSearchRequest = produce(searchRequest, (draft) => {
                    draft.sorts = [newSort];
                });
                this.props.sortSetting.setValue(value);
            }
        }

        newSearchRequest = produce(newSearchRequest, (draft) => {
            draft.pageNum = 0;
        });
        this.setState({ selected: newSelected }, () => {
            /* istanbul ignore else */
            if (typeof this.props.onSetActiveRowIndex === "function")
                this.props.onSetActiveRowIndex(-1);
            this._fetch(newSearchRequest, true);
        });
    };

    _onFilterBarClear = () => {
        const { searchRequest, searchItem } = this.props;
        this.setState(
            {
                selected: [],
            },
            () => {
                if (searchItem) {
                    this._fetch(this._prepSearchRequest(searchItem), true);
                    return;
                }

                let newSearchRequest = searchRequest;
                newSearchRequest = produce(newSearchRequest, (draft) => {
                    draft.filters = [];
                });
                newSearchRequest = produce(newSearchRequest, (draft) => {
                    draft.sorts = [];
                });
                // TODO FilterBarClear should remove ALL BUT HIDDEN FILTERS!
                this._fetch(newSearchRequest, true);
            }
        );
    };

    _onFilterTextChange = (filterText: string) => {
        const { searchRequest } = this.props;

        const newSearchRequest = produce(searchRequest, (draft) => {
            /* istanbul ignore else */
            if (searchRequest.isCustom === false) {
                draft.isCustom = true;
            }
            const idx = draft.filters.findIndex(
                (f) => f.name === "$SEARCHTEXT$"
            );
            if (idx > -1) {
                draft.filters[idx].value = filterText;
            }
            draft.searchText = filterText;
        });

        this._fetch(newSearchRequest, true);
    };

    _onClick = (
        e: SyntheticMouseEvent<*>,
        rowIndex: number,
        row: SearchResultRowDTO
    ) => {
        const prevRowIndex =
            this.props.sr?.currentRow || this.props.activeRowIndex;
        this.props.pagination?.setCurrent?.(
            rowIndex,
            e.ctrlKey || e.shiftKey ? null : row
        );
        // custom row click handler, if provided (no multiSelection)
        if (typeof this.props.onRowClick === "function") {
            this.props.onRowClick(e, rowIndex, row);
            return;
        }
        if (e.ctrlKey) {
            /* istanbul ignore else */
            if (!this.props.multiSelection?.multiple && prevRowIndex > -1) {
                this.props.multiSelection?.actions?.handleToggle?.(
                    prevRowIndex
                );
            }
            this.props.multiSelection?.actions?.handleToggle?.(rowIndex);
        } else if (e.shiftKey) {
            this.props.multiSelection?.actions.handleSelectRange(
                prevRowIndex,
                rowIndex
            );
        } else {
            /* istanbul ignore else */
            if (row.isDoc) {
                e.stopPropagation();
                CommandActionHandler("doc_preview", {
                    itemUri: row.itemUri,
                    newWindow: e.altKey,
                    searchTerm: (row.foundTerms || this.props.terms)
                        .join(" ")
                        .replace("*", ""),
                    ...this.props.pagination?.getProps?.(
                        rowIndex,
                        this._setCurrentRow
                    ),
                });
                return;
            }
            if (this.props.multiSelection?.multiple) {
                this.props.multiSelection?.disable?.();
                e.stopPropagation();
            }
        }
    };

    _onMoreClick = (
        e: SyntheticMouseEvent<*>,
        rowIndex: number,
        row: SearchResultRowDTO
    ) => {
        e.preventDefault();

        this.props.pagination?.setCurrent?.(rowIndex);
        if (
            this.props.multiSelection?.multiple &&
            Object.keys(
                this.props.multiSelection?.activeRows ??
                    /* istanbul ignore next */ {}
            ).length > 1 &&
            Object.prototype.hasOwnProperty.call(
                this.props.multiSelection?.activeRows,
                rowIndex
            )
        ) {
            openMultiSelectionActionPanel({
                ...this.props.pagination?.getProps?.(rowIndex),
                activeRows: this.props.multiSelection?.activeRows,
                targetView: "searchresult",
                mapper: /* istanbul ignore next */ (row) => ({
                    name: row.title,
                    ext: row.ext,
                    itemUri: row.itemUri,
                    formatId: row.breadcrumb?.formatId,
                    isDoc: row.isDoc,
                }),
            });
        } else {
            openSearchResultActionPanel({
                row: { ...row, targetView: "searchresult" },
                ...this.props.pagination?.getProps?.(rowIndex),
            });
        }
    };
    //#endregion

    _maybeChangeViewMode = (newViewMode: ?ListViewModeEnum): void => {
        let viewMode, fallbackViewMode;

        /** Decide if the current or future ViewMode might be changed */
        if (newViewMode) {
            viewMode = newViewMode;
        } else {
            viewMode = this.props.viewModeSetting.value;
        }
        /** Set fallback ViewMode if current/future ViewMode is not supported */
        if (!SupportGeoMapViewer() && viewMode === ListViewMode.Map) {
            console.warn(
                `GeoMapViewer support is disabled. Using fallback viewMode "${ListViewMode.List}"`
            );
            fallbackViewMode = ListViewMode.List;
        }
        if (
            !isFeatureOn(Feature.searchCalendar) &&
            viewMode === ListViewMode.Calendar
        ) {
            console.warn(
                `CalendarViewer support is disabled. Using fallback viewMode "${ListViewMode.List}"`
            );
            fallbackViewMode = ListViewMode.List;
        }
        /** Change ViewMode if there is a fallbackViewMode or a newViewMode*/
        /* istanbul ignore else */
        if (fallbackViewMode) {
            this.props.viewModeSetting.setValue(fallbackViewMode);
        } else if (
            this.props.viewModeSetting &&
            newViewMode !== this.props.viewModeSetting.value
        ) {
            this.props.viewModeSetting.setValue(newViewMode);
        }
    };

    _onViewModeChange = (viewMode: ListViewModeEnum): void =>
        this._maybeChangeViewMode(viewMode);

    _getAvailableViewModes = () => {
        const viewModes = [ListViewMode.List, ListViewMode.Grid];
        if (SupportGeoMapViewer()) viewModes.push(ListViewMode.Map);
        if (isFeatureOn(Feature.searchCalendar))
            viewModes.push(ListViewMode.Calendar);
        return viewModes;
    };

    _onCountOfGeoResults = (count: number): void =>
        this._updateToggleCounter(count, false);

    _handleMoreRowsLoaded = (
        rows: Array<any>,
        totalCount: number = 0,
        updateTotalCount = false,
        additionalState = {}
    ) => {
        // Store documents for pagination / multi selection
        const availableRows = {};
        for (let index in rows) {
            availableRows[index] = { ...rows[index] };
        }

        // Update multiSelection available Rows
        /* istanbul ignore next */
        this.props.multiSelection?.setAvailableRows?.(availableRows);

        // Update pagination
        /* istanbul ignore next */
        this.props.pagination?.onRowsLoaded?.(rows, totalCount);

        this.setState(additionalState);
        if (updateTotalCount) this._updateToggleCounter(totalCount);
    };

    _isRowActive = (rowIndex: number, activeState: boolean): boolean => {
        return this.props.multiSelection?.multiple
            ? this.props.multiSelection?.isSelected?.(rowIndex)
            : activeState;
    };

    render() {
        const {
            status,
            error,
            result,
            terms,
            activeRowIndex,
            isBreadcrumbReadonly,
        } = this.props;
        const { scrollToRow } = this.state;
        const isNoData =
            this.props.searchItem == null &&
            result == null &&
            status === Constants.NONE;
        const isCustomError =
            status === Constants.ERROR &&
            error?.extensions?.eventId === 1500; /*SEARCH_PARSE*/

        const isLoading = !(status === Constants.OK && result != null);
        const isOk = !(isLoading || isCustomError || isNoData);

        return (
            <div className={styles.wrapper}>
                {this.props.showFilterBar !== false && (
                    <SearchResultFilterBar
                        aggregations={this.state.aggregations}
                        selected={this.state.selected}
                        onSelect={this._onFilterBarSelect}
                        onClear={this._onFilterBarClear}
                        viewMode={this.props.viewModeSetting.value}
                        viewModes={this._getAvailableViewModes()}
                        onViewModeChange={this._onViewModeChange}
                        searchText={this.props.searchRequest?.searchText || ""}
                        onFilterTextChange={this._onFilterTextChange}
                    />
                )}
                {isNoData && <NoData locContext="search" />}
                {isCustomError && (
                    <NoData locContext="search">
                        <div>
                            <br />
                            Error parsing, field:
                            <b>{error?.extensions?.extra?.field}</b>, value:
                            <b>{error?.extensions?.extra?.value}</b>
                            <br />
                            <br />
                            {error.message}
                        </div>
                    </NoData>
                )}
                {isLoading && !(isCustomError || isNoData) && (
                    <Status status={status || Constants.LOADING} />
                )}
                {isOk &&
                    this.props.viewModeSetting.value === ListViewMode.Map && (
                        <SearchResultMap
                            mapProvider={this.props.mapProvider}
                            onCountOfGeoResults={this._onCountOfGeoResults}
                            loadMoreRows={this.loadMoreRows}
                            onClick={this.props.onMapClick}
                        />
                    )}

                {isOk &&
                    this.props.viewModeSetting.value ===
                        ListViewMode.Calendar && (
                        <SearchResultCalendar
                            loadMoreRows={this.loadMoreRows}
                            onClick={this._onClick}
                            onMoreClick={
                                this.props.isMoreHidden
                                    ? null
                                    : this._onMoreClick
                            }
                        />
                    )}

                {isOk &&
                    this.props.viewModeSetting.value === ListViewMode.Grid && (
                        <SearchResultGrid
                            terms={terms}
                            result={result}
                            loadMoreRows={this.loadMoreRows}
                            onClick={this._onClick}
                            onMoreClick={this._onMoreClick}
                            onContextClick={this._onMoreClick}
                            onFilterBarSelect={this._onFilterBarSelect}
                            activeRowIndex={activeRowIndex}
                            scrollToRow={scrollToRow}
                            searchRequest={this.props.searchRequest}
                            onSetActiveRowIndex={this.props.onSetActiveRowIndex}
                            isMoreHidden={this.props.isMoreHidden}
                            handleMoreRowsLoaded={this._handleMoreRowsLoaded}
                            isRowActive={this._isRowActive}
                            refresh={this.props.multiSelection?.activeRows}
                            columnMapper={this.state.columnMapper}
                            onReorder={this.onReorder}
                            colWidths={this.state.storedColWidths}
                        />
                    )}
                {isOk &&
                    this.props.viewModeSetting.value === ListViewMode.List && (
                        <SearchResult
                            terms={terms}
                            result={result}
                            loadMoreRows={this.loadMoreRows}
                            onClick={this._onClick}
                            onMoreClick={
                                this.props.isMoreHidden
                                    ? null
                                    : this._onMoreClick
                            }
                            activeRowIndex={activeRowIndex}
                            scrollToRow={scrollToRow}
                            isBreadcrumbReadonly={isBreadcrumbReadonly}
                            handleMoreRowsLoaded={this._handleMoreRowsLoaded}
                        />
                    )}
            </div>
        );
    }
}

const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => ({
    status: s.searchStatusSelector(state),
    error: s.searchErrorSelector(state),
    searchRequest: s.searchRequestSelector(state),
    terms:
        s.searchStatusSelector(state) === Constants.OK
            ? s.searchTermsSelector(state)
            : [""],
    result:
        s.searchStatusSelector(state) === Constants.OK
            ? s.searchResultSelector(state)
            : undefined,
    ...ownProps,
});

export const SearchResultContainerWithMultiSelection = compose(
    withRouter,
    connect(mapStateToProps),
    SettingValue({
        name: "searchResultViewMode",
        namespace: "viewModeSetting",
        defaultValue: ListViewMode.List,
    }),
    SettingValue({
        name: "searchResultSort",
        namespace: "sortSetting",
        defaultValue: "",
    }),
    TrackCurrentRow({
        namespace: "sr",
        defaultCurrentRow: 0,
    }),
    withMultiSelection({
        rowTrackerNamespace: "sr",
        delay: 600,
    })
)(SearchResultContainer);

export default compose(
    withRouter,
    connect(mapStateToProps),
    SettingValue({
        name: "searchResultViewMode",
        namespace: "viewModeSetting",
        defaultValue: ListViewMode.List,
    }),
    SettingValue({
        name: "searchResultSort",
        namespace: "sortSetting",
        defaultValue: "",
    })
)(SearchResultContainer);
